import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {IUser} from '../auth';
import {StorageService} from '../../services/storage/storage.service';
import {IAppointment} from '../appointment';
import {IPatient} from '../patient';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {firstValueFrom} from 'rxjs';
import {ITours} from './tour.interface';
import {IMealPlanResponse, MealPlanActions} from '../meal-plans';
import {ICredentials} from '../../interfaces/credentials';
import {LocalStorageEnum} from '../../enums/utils';

export interface IRTour {
  tour: ITours | null;
  demoPatient: IPatient;
  demoMealPlan?: IMealPlanResponse;
  demoAppointment: IAppointment;
}


export namespace TourAction {
  export class CompleteAll {
    static readonly type = '[Tour] Complete All Tour Action';
    constructor(public clinicId: string, public deleteDemoData = true) { }
  }
  export class CompleteOne {
    static readonly type = '[Tour] Complete Tour Action';
    constructor(public clinicId: string, public tour: string, public deleteDemoData = true) { }
  }

  export class Force {
    static readonly type = '[Tour] Force Tour Action';
    constructor(public clinicId: string, public tour: string) { }
  }


  export class Get {
    static readonly type = '[Tour] Get Tour Action';
    constructor() { }
  }

  export class CreateDemoPatient {
    static readonly type = '[Tour] Create Patient Demo Tour Action';
    constructor(public clinicId: string) { }
  }

  export class DeleteDemoPatient {
    static readonly type = '[Tour] Complete Clinic Tours Action';
    constructor(public clinicId: string) { }
  }

  export class CreateDemoMealPlan {
    static readonly type = '[Tour] Create Meal Plan Demo Tour Action';
    constructor(public clinicId: string) { }
  }

  export class CreateDemoAppointment {
    static readonly type = '[Tour] Create Appointment Demo Tour Action';
    constructor(public clinicId: string) { }
  }

  export class ResetState {
    static readonly type = '[Tour] Reset Demo Data Action';
    constructor() { }
  }
}



@State<IRTour>({
  name: 'tourState',
  defaults: {
    tour: null,
    demoPatient: null,
    demoAppointment: null
  },
})
@Injectable()
export class TourState {
  constructor(
    private baseService: BaseService<any>,
    private storageService: StorageService,
    private utilitiesServiceService: UtilitiesService
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getTour(state: IRTour): ITours | null {
    return state.tour;
  }

  @Selector()
  public static getDemoPatientCreated(state: IRTour): IPatient {
    return state.demoPatient;
  }

  @Selector()
  public static getDemoMealPlan(state: IRTour): IMealPlanResponse {
    return state.demoMealPlan;
  }

  @Selector()
  public static getDemoAppointmentCreated(state: IRTour): IAppointment {
    return state.demoAppointment;
  }

  @Action(TourAction.CompleteAll)
  async completeAllTourAction(ctx: StateContext<IRTour>, action?: TourAction.CompleteAll): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(
        `${this.url}/demo-data/${action.clinicId}/tours/complete`,
        void 0,
        void 0,
        void 0, {
          params: {
            deleteDemoData: action.deleteDemoData,
          }
        }
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(TourAction.CompleteOne)
  async completeTourAction(ctx: StateContext<IRTour>, action?: TourAction.CompleteOne): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(
        `${this.url}/demo-data/${action.clinicId}/tours/${action.tour}/complete`,
        void 0,
        void 0,
        void 0,
        {
          params: {
            deleteDemoData: action.deleteDemoData,
          }
        }
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(TourAction.Force)
  async forceTourAction(ctx: StateContext<IRTour>, action?: TourAction.Force): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/demo-data/${action.clinicId}/tours/force`, {name: action.tour})
    );
    if (response.error) throw new Error(response.message);
  }

  @Action(TourAction.DeleteDemoPatient)
  async completeAppointmentTourAction(ctx: StateContext<IRTour>, action?: TourAction.DeleteDemoPatient): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/demo-data/${action.clinicId}/tours/demoPatient`)
  );

    if (response.error) throw new Error(response.message);
  }

  @Action(TourAction.Get)
  async getTourAction(ctx: StateContext<IRTour>, _?: TourAction.Get): Promise<void> {
    const userAndToken: ICredentials = this.storageService.getLocalItem<{ user: IUser, token: string }>('user');
    if (!userAndToken) return;
    if ((!userAndToken || !userAndToken.user || !userAndToken.token) || (!userAndToken?.user?.patients && !userAndToken?.user?.experts)) return;

    const clinicId: string = userAndToken.user.experts ? userAndToken.user.experts[0].clinicId : userAndToken.user.patients[0].clinicId;

    const response: DataBaseServiceResponse<ITours> = await firstValueFrom(
      this.baseService.get(`${this.url}/demo-data/${clinicId}/tours`)
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      tour: response.entity
    });

    // Set local user
    userAndToken.user = {
      ...userAndToken.user,
      metadata: {
        ...(userAndToken.user?.metadata ?? {}),
        tours: response.entity,
      }
    };
    this.storageService.setLocalItem(LocalStorageEnum.USER, userAndToken);
  }

  @Action(TourAction.CreateDemoPatient)
  async createPatientDemoTourAction(ctx: StateContext<IRTour>, action?: TourAction.CreateDemoPatient): Promise<void> {
    const response: DataBaseServiceResponse<IPatient> = await firstValueFrom(
      this.baseService.post(`${this.url}/demo-data/${action.clinicId}/createPatient`, void 0)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      demoPatient: response.entity
    });
  }

  @Action(TourAction.CreateDemoMealPlan)
  async createMealPlanDemoTourAction(ctx: StateContext<IRTour>, action?: TourAction.CreateDemoMealPlan): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom(
      this.baseService.post(`${this.url}/demo-data/${action.clinicId}/create-demo-meal-plan`, {})
    );

    if (response.error) throw new Error(response.message);
    // Set Demo MealPlan to MealPlanList
    await firstValueFrom(ctx.dispatch(new MealPlanActions.Set(response.entity)));

    ctx.patchState({
      demoMealPlan: response.entity
    });

  }

  @Action(TourAction.CreateDemoAppointment)
  async createAppointmentDemoTourAction(ctx: StateContext<IRTour>, action?: TourAction.CreateDemoAppointment): Promise<void> {
    const timeZone: string = this.utilitiesServiceService.getTimeZoneBrowser();
    const currentDate: Date = new Date();
    const date = {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth(),
      day: currentDate.getDate(),
      hour: currentDate.getHours(),
      minute: currentDate.getMinutes()
    };
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom(
      this.baseService.post(
        `${this.url}/demo-data/${action.clinicId}/createAppointment`,
        {timeZone, date}
      )
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      demoAppointment: {
        ...response.entity,
      }
    });
    this.storageService.setLocalItem('appointmentDemoDate', response.entity?.startDate);
  }

  @Action(TourAction.ResetState)
  resetDemoDataAction({patchState}: StateContext<IRTour>, {}: TourAction.ResetState): void {
    patchState({
      demoPatient: null,
      demoAppointment: null,
      demoMealPlan: null,
    });
  }
}
